import { useCallback, useState, useRef, useEffect } from "react"
import { authHeaders } from "@/services/api.client"
import { env } from "@/lib/env"
import { resolveApiUrl } from "@/lib/api-url"
import { gateFromResponse } from "@/lib/quotaGate"

export type LegalResearchPhase = "idle" | "pending" | "answered" | "error"

export interface LegalCitation {
  label: string
  source_type: string
  reference_number: string
  title: string
  url: string
}

export interface LegalResearchAnswer {
  answer_text: string
  grounding_decision: string
  used_web: boolean
  evidence: Record<string, unknown>
  citations: LegalCitation[]
}

interface LegalResearchStreamHandle {
  phase: LegalResearchPhase
  progressLog: string[]
  answer: LegalResearchAnswer | null
  error: string | null
  ask: (query: string) => Promise<LegalResearchAnswer | null>
  reset: () => void
}

async function* parseSSEEvents(
  stream: ReadableStream<Uint8Array>
): AsyncGenerator<{ type: string; payload: Record<string, unknown> }> {
  const reader = stream.getReader()
  const decoder = new TextDecoder()
  let buffer = ""

  try {
    while (true) {
      let readResult
      try {
        readResult = await reader.read()
      } catch (err: any) {
        if (err.name === "AbortError") {
          break
        }
        throw err
      }
      const { done, value } = readResult
      if (done) break
      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split("\n")
      buffer = lines.pop() ?? ""
      for (const line of lines) {
        const trimmed = line.trim()
        if (!trimmed.startsWith("data:")) continue
        const raw = trimmed.slice(5).trim()
        if (!raw) continue
        try {
          const parsed = JSON.parse(raw) as { type?: string; payload?: Record<string, unknown> }
          if (parsed.type) yield { type: parsed.type, payload: parsed.payload ?? {} }
        } catch {
          // malformed JSON — skip
        }
      }
    }
  } finally {
    reader.releaseLock()
  }
}

const UNAVAILABLE_MESSAGE = "Legal research is temporarily unavailable. Please try again."

export function useLegalResearchStream(): LegalResearchStreamHandle {
  const [phase, setPhase] = useState<LegalResearchPhase>("idle")
  const [progressLog, setProgressLog] = useState<string[]>([])
  const [answer, setAnswer] = useState<LegalResearchAnswer | null>(null)
  const [error, setError] = useState<string | null>(null)

  const abortControllerRef = useRef<AbortController | null>(null)

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  const reset = useCallback(() => {
    setPhase("idle")
    setProgressLog([])
    setAnswer(null)
    setError(null)
  }, [])

  const ask = useCallback(
    async (query: string): Promise<LegalResearchAnswer | null> => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
      abortControllerRef.current = new AbortController()

      setPhase("pending")
      setProgressLog([])
      setAnswer(null)
      setError(null)

      try {
        const response = await fetch(
          resolveApiUrl(env.VITE_API_URL, "/api/v1/legal-research/stream"),
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...authHeaders(),
            },
            body: JSON.stringify({ query, use_web_grounding: true }),
            signal: abortControllerRef.current.signal,
          }
        )

        if (!response.ok || !response.body) {
          setPhase("error")
          setError(`Legal research request failed: ${response.status}`)
          if (response.status === 402) {
            const entitlementError = new Error("Research entitlement required") as Error & {
              status: number
              gated?: boolean
            }
            entitlementError.status = response.status
            // Show the server's own denial (limit, reset time) when the body carries one.
            entitlementError.gated = (await gateFromResponse(response)) !== null
            throw entitlementError
          }
          return null
        }

        let resolvedAnswer: LegalResearchAnswer | null = null

        for await (const event of parseSSEEvents(response.body)) {
          if (event.type === "progress") {
            const message = event.payload.message as string | undefined
            if (message) setProgressLog((prev) => [...prev, message])
          } else if (event.type === "answer") {
            resolvedAnswer = event.payload as unknown as LegalResearchAnswer
            setAnswer(resolvedAnswer)
            setPhase("answered")
          } else if (event.type === "error") {
            setError(UNAVAILABLE_MESSAGE)
            setPhase("error")
            return null
          }
        }

        return resolvedAnswer
      } catch (err: any) {
        if (err.name === "AbortError") {
          return null
        }
        if (err && err.status === 402) throw err
        setPhase("error")
        setError(UNAVAILABLE_MESSAGE)
        return null
      }
    },
    []
  )

  return { phase, progressLog, answer, error, ask, reset }
}
